// import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import Framework, { shapes, LoadingHandler } from '@greenville/framework';
import { FormattedMessage } from 'react-intl';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import { toJS } from 'mobx';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Typography,
  Radio,
  RadioGroup,
  TableBody,
  FormControlLabel,
  TextField,
  Box,
  Grid,
  Button,
  TextareaAutosize,
  withStyles
} from '@material-ui/core';
import { inject, observer } from 'mobx-react';
import * as constants from '../../../../common/constants';
import TenantFeatureModel from '../../models/TenantFeature';
import Loader from '../../../../common/Loader';
import UserPermissionCheck from '../../models/UserPermissionCheck';
import UsersPermissions from '../../../users/models/UsersPermissions';
import TenantConfigModel from '../../models/TenantConfig';

const themes = createTheme({
  palette: {
    primary: {
      light: '#047a9c',
      main: '#005d83',
      dark: '#003558',
      contrastText: '#ffffff'
    }
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 30
      }
    }
  }
});

const styles = () => ({
  tableHeader: {
    tableLayout: 'fixed',
    width: '100%'
  },
  tableTitleWidth: {
    width: 1
  },
  columnStyle: {
    fontFamily: 'Open Sans, Calibri, Tahoma, sans-serif',
    fontWeight: '600'
  },
  radioButton: {
    textAlign: 'center'
  },
  buttonStyle: {
    cursor: 'pointer',
    width: '170px',
    margin: '10px',
    fontSize: '14px',
    background: '#005d83 !important'
  },
  disabledButtonStyle: {
    cursor: 'pointer',
    width: '170px',
    margin: '10px',
    fontSize: '14px'
  },
  root: {
    width: '100%'
  },
  tableText: {
    textAlign: 'center',
    color: '#6a7070',
    fontSize: 14
  },
  tableTextAlongWidth: {
    textAlign: 'center',
    color: '#6a7070',
    fontSize: 14,
    width: 160
  },
  columnWidth: {
    width: 200
  },
  contentText: {
    textAlign: 'center',
    color: '#6a7070',
    fontSize: 14,
    width: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  flag: {
    justifyItems: 'center'
  },
  gridColumns: {
    display: 'flex',
    justifyContent: 'center'
  },
  mainContainer: {
    textAlign: 'end',
    marginBottom: '5px'
  },
  textAreaSize: {
    width: '80%',
    borderWidth: '0.1rem',
    borderRadius: '0.3rem'
  },
  paperArea: {
    padding: '10px 10px 0px 10px'
  },
  heading: {
    // display: 'flex',
    // width: '65%',
    // justifyContent: 'flex-end',
    padding: '15px 0px 25px 0px'
  },
  headerGrid: {
    justifyContent: 'space-evenly',
    paddingTop: '10px'
  },
  headerButton: {
    padding: '0px 10px 20px 10px'
  }
});

/* eslint-disable no-shadow */
function TenantFeatureGrid(props) {
  const {
    classes,
    tenantFeatureModel,
    tenantFeatureServiceStatus,
    tenantFeatureUpdateServiceStatus,
    userPermissions,
    userPermissionCheck,
    tenantConfig,
    language
  } = props;
  const history = useHistory();
  const [data, setData] = useState(Array.isArray(tenantFeatureModel) ? tenantFeatureModel : []);
  const [updatedFeature, setUpdatedFeature] = useState([]);
  const [orderDirection, setOrderDirection] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const permissionsList = toJS(userPermissions.permissionList.permissions);

  const getTenantId = (keyName) => {
    let tenTId = '';
    const { data } = toJS(tenantConfig);
    if (data.length > 0) {
      const match = data.find((config) => config.name === keyName);
      if (match) {
        tenTId = match.tenantId;
      }
    }

    return tenTId;
  };

  useEffect(() => {
    const locationVal = history.location.pathname.split('/')[2];
    const tenantIdVal = getTenantId(locationVal);
    tenantFeatureModel.fetch({ tenantId: tenantIdVal });
    Framework.getEventManager().on(constants.SET_TENANT_FEATURE_DATA, () => {
      const { data } = toJS(tenantFeatureModel);
      if (data.length > 0) {
        setData(data);
      }
    });
  }, []);

  const handleChange = (index, changeKey, value) => {
    const updatedLocal = data.map((item) => (item.key === index ? { ...item, [changeKey]: value } : item));
    setData(updatedLocal);

    const updatedItem = updatedLocal.find((item) => item.key === index);
    const storeData = toJS(tenantFeatureModel);
    const originalItem = storeData.data.find((item) => item.key === index);
    const hasAnyChange = Object.keys(updatedItem).some((key) => updatedItem[key] !== originalItem[key]);
    const existing = updatedFeature.find((item) => item.key === updatedItem.key);
    if (hasAnyChange) {
      if (existing) {
        const replaced = updatedFeature.map((item) => (item.key === updatedItem.key ? updatedItem : item));
        setUpdatedFeature(replaced);
      } else {
        setUpdatedFeature([...updatedFeature, updatedItem]);
      }
    } else {
      /* eslint-disable no-lonely-if */
      if (existing) {
        const cleaned = updatedFeature.filter((item) => item.key !== updatedItem.key);
        setUpdatedFeature(cleaned);
      }
    }
  };

  const addCommentToUpdatedFeature = (key, comment) => {
    setData((prev) => prev.map((item) => (item.key === key ? { ...item, comment } : item)));
    setUpdatedFeature((prev) => {
      const existing = prev.find((item) => item.key === key);
      if (!existing) return prev;
      if (existing.comment === comment) return prev;
      const updated = { ...existing, comment };
      return prev.map((item) => (item.key === key ? updated : item));
    });
  };

  const handleSave = () => {
    const locationVal = history.location.pathname.split('/')[2];
    const tenantIdVal = getTenantId(locationVal);
    const { data } = toJS(tenantFeatureModel);
    const cleanedData = updatedFeature.filter((updatedItem) => {
      const originalItem = data.find((item) => item.key === updatedItem.key);
      return !originalItem || updatedItem.value !== originalItem.value;
    });
    tenantFeatureModel.updateTenantFeature(cleanedData, tenantIdVal);
    setUpdatedFeature(cleanedData);
  };

  const handleReset = () => {
    const { data } = toJS(tenantFeatureModel);
    setData(data);
    setUpdatedFeature([]);
  };

  const sortedRows = [...data].sort((a, b) => {
    const valueA = a[orderBy]?.toString().toLowerCase() || '';
    const valueB = b[orderBy]?.toString().toLowerCase() || '';

    if (valueA < valueB) return orderDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return orderDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  return (
    <>
      <LoadingHandler
        loading={tenantFeatureServiceStatus.isPending || tenantFeatureUpdateServiceStatus.isPending}
        loadingContent={<Loader />}
        content={
          <>
            <Paper className={classes.paperArea}>
              <Grid container spacing={2} className={classes.headerGrid}>
                <Grid size={8} className={classes.heading}>
                  <Typography variant="h5" className={classes.columnStyle} gutterBottom spacing={9}>
                    <FormattedMessage {...language.getText('tenantFeature.HEADER')} />
                  </Typography>
                </Grid>
                {/* <Grid size={4} className={classes.headerButton}>
                  <ThemeProvider theme={themes}>
                    <Button
                      className={classes.buttonStyle}
                      variant="contained"
                      size="small"
                      color="primary"
                      display="none"
                      onClick={(e) => handleAddNewFeature(e)}
                    >
                      + Add Base Feature
                    </Button>
                  </ThemeProvider>
                </Grid> */}
              </Grid>

              <TableContainer className={classes.root} component={Paper}>
                <Table stickyHeader className={classes.tableHeader}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" className={classes.tableTitleWidth} style={{ width: '32%' }}>
                        <Typography variant="h6" className={classes.columnStyle}>
                          <FormattedMessage {...language.getText('tenantFeature.FEATURE')} />
                          <TableSortLabel
                            active
                            direction={orderBy === 'name' ? orderDirection : 'asc'}
                            onClick={() => handleSort('name')}
                            sx={{
                              // disable cell selection style
                              '.MuiDataGrid-cell:focus': {
                                outline: 'none'
                              },
                              // pointer cursor on ALL rows
                              '& .MuiTableSortLabel-root:hover': {
                                cursor: 'pointer'
                              }
                            }}
                          />
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className={classes.tableTitleWidth} style={{ width: '24%' }}>
                        <Typography variant="h6" className={classes.columnStyle}>
                          <FormattedMessage {...language.getText('tenantFeature.VALUE')} />
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className={classes.columnStyle} style={{ width: '12%' }}>
                        <Typography variant="h6" className={classes.columnStyle}>
                          <FormattedMessage {...language.getText('tenantFeature.OVERRIDDEN')} />
                          <TableSortLabel
                            active
                            direction={orderBy === 'overridden' ? orderDirection : 'asc'}
                            onClick={() => handleSort('overridden')}
                            sx={{
                              // disable cell selection style
                              '.MuiDataGrid-cell:focus': {
                                outline: 'none'
                              },
                              // pointer cursor on ALL rows
                              '& .MuiTableSortLabel-root:hover': {
                                cursor: 'pointer'
                              }
                            }}
                          />
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className={classes.tableTitleWidth} style={{ width: '32%' }}>
                        <Typography variant="h6" className={classes.columnStyle}>
                          <FormattedMessage {...language.getText('tenantFeature.COMMENT')} />
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <>
                      {sortedRows.length > 0 &&
                        sortedRows.map((row) => (
                          <TableRow
                            key={row.key}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 }, textAlign: 'center' }}
                          >
                            <TableCell component="th" scope="row" align="center">
                              {row.name.toUpperCase()}
                            </TableCell>
                            <TableCell align="center" className={classes.flag}>
                              <ThemeProvider theme={themes}>
                                {typeof row.value === 'string' ? (
                                  <TextField
                                    label=""
                                    margin="dense"
                                    name="userId"
                                    onChange={(e) => handleChange(row.key, 'value', e.target.value)}
                                    style={{ minWidth: '32%' }}
                                    value={row.value}
                                    variant="outlined"
                                  />
                                ) : (
                                  <RadioGroup
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    defaultValue="mcq"
                                    name={row.key}
                                    value={row.value ? 'true' : 'false'}
                                    onChange={(e) => handleChange(row.key, 'value', e.target.value === 'true')}
                                    defaultChecked
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'row'
                                    }}
                                  >
                                    <FormControlLabel value="true" control={<Radio color="primary" />} label="ON" />
                                    <FormControlLabel value="false" control={<Radio color="primary" />} label="OFF" />
                                  </RadioGroup>
                                )}
                              </ThemeProvider>
                            </TableCell>
                            <TableCell component="th" scope="row" align="center">
                              {row.overridden}
                            </TableCell>
                            <TableCell>
                              <ThemeProvider theme={themes}>
                                <Grid item xs={12} className={classes.gridColumns}>
                                  <TextareaAutosize
                                    className={classes.textAreaSize}
                                    id={row.key}
                                    name={row.key}
                                    variant="outlined"
                                    aria-label="maximum height"
                                    onChange={(e) => addCommentToUpdatedFeature(e.target.name, e.target.value)}
                                    minRows={6}
                                    maxRows={3}
                                    value={row.comment}
                                  />
                                </Grid>
                              </ThemeProvider>
                            </TableCell>
                          </TableRow>
                        ))}
                    </>
                  </TableBody>
                </Table>
              </TableContainer>
              <br />
              <Box sx={{ flexGrow: 1 }}>
                <Grid container justify="center" className={classes.buttonGroupStyle}>
                  <ThemeProvider theme={themes}>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => handleSave()}
                      disabled={userPermissionCheck?.isPermissionCheck && !permissionsList.includes('tenant_admin')}
                      className={
                        userPermissionCheck?.isPermissionCheck && !permissionsList.includes('tenant_admin')
                          ? classes.disabledButtonStyle
                          : classes.buttonStyle
                      }
                    >
                      {constants.SAVE}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => handleReset()}
                      disabled={userPermissionCheck?.isPermissionCheck && !permissionsList.includes('tenant_admin')}
                      className={
                        userPermissionCheck?.isPermissionCheck && !permissionsList.includes('tenant_admin')
                          ? classes.disabledButtonStyle
                          : classes.buttonStyle
                      }
                    >
                      {constants.CHAT_RESET}
                    </Button>
                  </ThemeProvider>
                </Grid>
                <br />
              </Box>
            </Paper>
          </>
        }
      />
    </>
  );
}

TenantFeatureGrid.propTypes = {
  language: shapes.language.isRequired,
  classes: PropTypes.object.isRequired,
  tenantFeatureModel: shapes.modelOf(TenantFeatureModel).isRequired,
  tenantFeatureServiceStatus: shapes.state.isRequired,
  tenantFeatureUpdateServiceStatus: shapes.state.isRequired,
  userPermissions: shapes.modelOf(UsersPermissions).isRequired,
  userPermissionCheck: shapes.modelOf(UserPermissionCheck).isRequired,
  tenantConfig: shapes.modelOf(TenantConfigModel).isRequired
};

// TenantFeatureGrid.defaultProps = {
// };

export default withStyles(styles)(
  inject(
    'language',
    'tenantFeatureModel',
    'tenantFeatureServiceStatus',
    'tenantFeatureUpdateServiceStatus',
    'userPermissions',
    'userPermissionCheck',
    'tenantConfig'
  )(observer(TenantFeatureGrid))
);
